import { useCallback, useEffect, useRef, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import usePrevious from '@ancon/wildcat-utils/shared/hooks/usePrevious'
import isEqual from 'lodash/isEqual'

import { MenuSection } from '../../types'
import useAppDispatch from '../../../../store/hooks/useAppDispatch'
import { fetchOutletMenuGroups } from '../../../product/store/productThunks'
import getMenuTreeSections from '../../utils/getMenuTreeSections'
import {
  outletFiltersOrderFormatSelector,
  outletFiltersServiceTimeSelector,
  outletForceRefreshMenuSelector,
  outletShowProductsFromOtherOrderFormatsSelector,
} from '../../store/outletSelector'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import EmptyPlaceholder from '../../../app/components/emptyPlaceholder/EmptyPlaceholder'

import OutletMenuHeader from './OutletMenuHeader'
import OutletMenuSkeleton from './OutletMenuSkeleton'
import OutletMenuSection from './OutletMenuSection'

const MainMenuGroupScrollOffset = 180
const SubMenuGroupScrollOffset = 80

function getScrolledSubGroupIndex(
  sectionOffsets: number[],
  scrollPosition: number,
) {
  if (sectionOffsets.length && scrollPosition < sectionOffsets[0]) {
    return 0
  }
  if (
    sectionOffsets.length &&
    scrollPosition > sectionOffsets[sectionOffsets.length - 1]
  ) {
    return sectionOffsets.length - 1
  }
  const foundIndex = sectionOffsets.findIndex(
    offsetTop => scrollPosition < offsetTop,
  )
  return foundIndex > 1 ? foundIndex - 1 : 0
}

export default function OutletMenu() {
  const { t } = useTranslation('outlet')

  const dispatch = useAppDispatch()

  const headerRef = useRef<HTMLDivElement>(null)

  const isAutoScrolling = useRef(false)

  const subGroupOffsetTops = useRef<number[]>([])

  const groupOffsetTops = useRef<number[]>([])

  const prevScrollPositionRef = useRef<number>(0)

  const prevMenuHeaderHeightRef = useRef<number | undefined>()

  const orderFormat = useAppSelector(outletFiltersOrderFormatSelector)

  const serviceTime = useAppSelector(outletFiltersServiceTimeSelector)

  const forceRefreshMenu = useAppSelector(outletForceRefreshMenuSelector)

  const showProductsFromOtherOrderFormats = useAppSelector(
    outletShowProductsFromOtherOrderFormatsSelector,
  )

  const [isLoading, setIsLoading] = useState(true)

  const [menuSections, setMenuSections] = useState<MenuSection[]>([])

  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0)

  const [selectedSubGroupIndex, setSelectedSubGroupIndex] = useState<
    number | undefined
  >(0)

  const prevOrderFormat = usePrevious(orderFormat, true)

  const prevServiceTime = usePrevious(serviceTime, true)

  useEffect(() => {
    const didOrderFormatChange = prevOrderFormat !== orderFormat
    const didServiceTimeChange = !isEqual(prevServiceTime, serviceTime)
    const shouldForceRefreshMenu = forceRefreshMenu && !isLoading

    if (
      !!orderFormat &&
      (didOrderFormatChange || didServiceTimeChange || shouldForceRefreshMenu)
    ) {
      setIsLoading(true)
      dispatch(fetchOutletMenuGroups())
        .unwrap()
        .then(menuGroups => {
          setMenuSections(
            getMenuTreeSections(
              menuGroups,
              orderFormat,
              showProductsFromOtherOrderFormats,
            ),
          )
        })
        .catch(() => {
          setMenuSections([])
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    forceRefreshMenu,
    orderFormat,
    prevOrderFormat,
    prevServiceTime,
    serviceTime,
  ])

  useEffect(() => {
    function handleScroll() {
      if (headerRef.current && !isAutoScrolling.current) {
        const currentScrollPosition = document.body.scrollTop
        const menuHeaderHeight =
          headerRef.current.getBoundingClientRect().height
        const scrollPosWithHeader = currentScrollPosition + menuHeaderHeight

        /**
         * Calculate the height difference between the previous menu header and the current one
         * to adjust the previous scroll position when the header height changes.
         */
        let menuHeaderHeightDiff = 0
        if (prevMenuHeaderHeightRef.current) {
          menuHeaderHeightDiff =
            prevMenuHeaderHeightRef.current - menuHeaderHeight
        }
        const prevScrollPosition =
          prevScrollPositionRef.current - menuHeaderHeightDiff

        const isScrollingDown = currentScrollPosition > prevScrollPosition
        const isScrollingUp = currentScrollPosition < prevScrollPosition

        // Section index change
        const nextSectionIndex = getScrolledSubGroupIndex(
          groupOffsetTops.current,
          scrollPosWithHeader + MainMenuGroupScrollOffset,
        )

        if (
          nextSectionIndex >= 0 &&
          nextSectionIndex !== selectedSectionIndex &&
          /**
           * This prevents the section flickering when scrolling up and down
           * because of any menu header height changes.
           */
          ((isScrollingDown && nextSectionIndex > selectedSectionIndex) ||
            (isScrollingUp && nextSectionIndex < selectedSectionIndex))
        ) {
          setSelectedSectionIndex(nextSectionIndex)
        }

        const isNextSectionHasSubGroups =
          !!menuSections[nextSectionIndex]?.subGroups?.length

        if (isNextSectionHasSubGroups) {
          // Sub group index change
          const nextSubGroupIndex = getScrolledSubGroupIndex(
            subGroupOffsetTops.current,
            scrollPosWithHeader + SubMenuGroupScrollOffset,
          )

          if (
            nextSubGroupIndex >= 0 &&
            nextSubGroupIndex !== selectedSubGroupIndex
          ) {
            setSelectedSubGroupIndex(nextSubGroupIndex)
          }
        }

        prevScrollPositionRef.current = currentScrollPosition
        prevMenuHeaderHeightRef.current = menuHeaderHeight
      }
    }

    document.body.addEventListener('scroll', handleScroll)

    return () => {
      document.body.removeEventListener('scroll', handleScroll)
    }
  }, [menuSections, selectedSectionIndex, selectedSubGroupIndex])

  useEffect(() => {
    if (
      menuSections[selectedSectionIndex]?.subGroups?.length &&
      headerRef.current
    ) {
      const sectionOffsets = menuSections[selectedSectionIndex].subGroups?.map(
        (_, index) => {
          const element = document.getElementById(
            `group-${selectedSectionIndex}-sub-group-${index}`,
          )
          return element ? element.offsetTop : 0
        },
      )

      // section offsets
      if (subGroupOffsetTops?.current && sectionOffsets) {
        subGroupOffsetTops.current = sectionOffsets
      }
    }
  }, [menuSections, selectedSectionIndex])

  useEffect(() => {
    if (groupOffsetTops.current) {
      const sectionOffsets = Object.keys(menuSections).map((_, index) => {
        const element = document.getElementById(`group-${index}`)
        return element ? element.offsetTop : 0
      })

      // section offsets
      if (groupOffsetTops?.current && sectionOffsets) {
        groupOffsetTops.current = sectionOffsets
      }
    }
  }, [menuSections])

  useEffect(() => {
    function handleScrollEnd() {
      if (isAutoScrolling.current) {
        isAutoScrolling.current = false
        prevMenuHeaderHeightRef.current = undefined
        prevScrollPositionRef.current = document.body.scrollTop
      }
    }

    document.body.addEventListener('scrollend', handleScrollEnd)

    return () => {
      document.body.removeEventListener('scrollend', handleScrollEnd)
    }
  }, [])

  const handleOnStartScroll = useCallback(() => {
    isAutoScrolling.current = true
  }, [])

  function handleOnChangeGroup(index: number) {
    setSelectedSectionIndex(index)
    if (selectedSubGroupIndex !== undefined) {
      setSelectedSubGroupIndex(undefined)
    }
  }

  if (!isLoading && !menuSections.length) {
    return (
      <EmptyPlaceholder
        title={t('outletMenuEmptyPlaceholder.title')}
        message={t('outletMenuEmptyPlaceholder.message')}
      />
    )
  }

  return isLoading ? (
    <OutletMenuSkeleton />
  ) : (
    <>
      <OutletMenuHeader
        ref={headerRef}
        groups={menuSections}
        selectedGroupIndex={selectedSectionIndex}
        selectedSubGroupIndex={selectedSubGroupIndex}
        onChangeGroup={handleOnChangeGroup}
        onChangeSubGroup={setSelectedSubGroupIndex}
        onStartScroll={handleOnStartScroll}
      />
      {menuSections.map((menuSection, index) => (
        <OutletMenuSection
          key={menuSection.id}
          menuSection={menuSection}
          sectionIndex={index}
        />
      ))}
    </>
  )
}
